import { ReactNode } from 'react'

import {
  AWS_CHANNEL_STATUS,
  AWS_FLOW_STATUS,
  AWS_PIPELINE_STATUS
} from '@/constants/streamEvents/constants'
import { EventPipelineStatus } from '@/types/streams/eventPipelineStatus'

import {
  ProvisionedIndicator,
  ProvisioningIndicator,
  UnprovisionedIndicator
} from './EventSettingsActionsStatusIndicators'

const NO_STATUS = 'No Status'

export const pipelineProvisioned = (pipelineStatus: EventPipelineStatus | null) =>
  pipelineStatus?.pipelineStatus === AWS_PIPELINE_STATUS.PROVISIONED
export const pipelineProvisioning = (pipelineStatus: EventPipelineStatus | null) =>
  pipelineStatus?.pipelineStatus === AWS_PIPELINE_STATUS.PROVISIONING ||
  pipelineStatus?.pipelineStatus === AWS_PIPELINE_STATUS.DELETING

export const channelProvisioned = (
  pipelineStatus: EventPipelineStatus | null,
  channelIndex?: number
): boolean => {
  if (channelIndex !== undefined) {
    return pipelineStatus?.channels?.[channelIndex]?.state === AWS_CHANNEL_STATUS.RUNNING
  }
  return (
    pipelineStatus?.channels?.some((channel) => channel?.state === AWS_CHANNEL_STATUS.RUNNING) ||
    false
  )
}

export const channelProvisioning = (
  pipelineStatus: EventPipelineStatus | null,
  channelIndex?: number
): boolean => {
  if (channelIndex !== undefined) {
    return pipelineStatus?.channels?.[channelIndex]?.state === AWS_CHANNEL_STATUS.CREATED
  }
  return (
    pipelineStatus?.channels?.some((channel) => channel?.state === AWS_CHANNEL_STATUS.CREATED) ||
    false
  )
}

export const flowProvisioned = (
  pipelineStatus: EventPipelineStatus | null,
  flowIndex?: number
): boolean => {
  if (flowIndex !== undefined) {
    return pipelineStatus?.flows?.[flowIndex]?.status === AWS_FLOW_STATUS.ACTIVE
  }
  return pipelineStatus?.flows?.some((flow) => flow?.status === AWS_FLOW_STATUS.ACTIVE) || false
}

export const flowProvisioning = (
  pipelineStatus: EventPipelineStatus | null,
  flowIndex?: number
): boolean => {
  if (flowIndex !== undefined) {
    return (
      pipelineStatus?.flows?.[flowIndex]?.status === AWS_FLOW_STATUS.CREATED ||
      pipelineStatus?.flows?.[flowIndex]?.status === AWS_FLOW_STATUS.STARTING ||
      pipelineStatus?.flows?.[flowIndex]?.status === AWS_FLOW_STATUS.UPDATING ||
      pipelineStatus?.flows?.[flowIndex]?.status === AWS_FLOW_STATUS.RUNNING
    )
  }
  return (
    pipelineStatus?.flows?.some((flow) => flow?.status === AWS_FLOW_STATUS.CREATED) ||
    pipelineStatus?.flows?.some((flow) => flow?.status === AWS_FLOW_STATUS.STARTING) ||
    pipelineStatus?.flows?.some((flow) => flow?.status === AWS_FLOW_STATUS.UPDATING) ||
    pipelineStatus?.flows?.some((flow) => flow?.status === AWS_FLOW_STATUS.RUNNING) ||
    false
  )
}

const pipelineStatus = (pipelineStatus: EventPipelineStatus | null) =>
  pipelineStatus?.pipelineStatus || NO_STATUS

const flowStatus = (pipelineStatus: EventPipelineStatus | null, flowIndex?: number): string => {
  if (flowIndex !== undefined) {
    return pipelineStatus?.flows?.[flowIndex]?.status || NO_STATUS
  }
  return NO_STATUS
}

const channelStatus = (pipelineStatus?: EventPipelineStatus, channelIndex?: number): string => {
  if (channelIndex !== undefined) {
    return pipelineStatus?.channels?.[channelIndex]?.state || NO_STATUS
  }
  return NO_STATUS
}

const serviceMap = {
  pipelineProvisioned,
  pipelineProvisioning,
  pipelineStatus,
  channelProvisioned,
  channelProvisioning,
  channelStatus,
  flowProvisioned,
  flowProvisioning,
  flowStatus
}

const isServiceProvisioned = (
  awsService: 'pipeline' | 'channel' | 'flow',
  pipelineStatus: EventPipelineStatus,
  index?: number
) =>
  serviceMap[(awsService + 'Provisioned') as keyof typeof serviceMap](pipelineStatus || '', index)
const isServiceProvisioning = (
  awsService: 'pipeline' | 'channel' | 'flow',
  pipelineStatus: EventPipelineStatus,
  index?: number
) => serviceMap[(awsService + 'Provisioning') as keyof typeof serviceMap](pipelineStatus, index)

export const getStatusIndicator = (
  awsService: 'pipeline' | 'channel' | 'flow',
  pipelineStatus: EventPipelineStatus | null,
  index?: number
): ReactNode => {
  if (!pipelineStatus) return <UnprovisionedIndicator />
  const isProvisioned = isServiceProvisioned(awsService, pipelineStatus, index)
  const isProvisioning = isServiceProvisioning(awsService, pipelineStatus, index)
  if (isProvisioned) {
    return <ProvisionedIndicator />
  } else if (isProvisioning) {
    return <ProvisioningIndicator />
  }
  return <UnprovisionedIndicator />
}

export const getStatusText = (
  awsService: 'pipeline' | 'channel' | 'flow',
  pipelineStatus: EventPipelineStatus | null,
  index?: number
): ReactNode => {
  if (!pipelineStatus) return NO_STATUS
  const service = (awsService + 'Status') as keyof typeof serviceMap
  return serviceMap[service](pipelineStatus, index)
}
